import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { nhost } from "@/lib/nhost";

type AppStatus = "active" | "maintenance" | "closed";

const STATUS_KEY = "mad_app_status";
const STATUS_CACHE_MS = 5 * 60 * 1000; // 5 minutes

let cachedStatus: AppStatus | null = null;
let lastFetch = 0;

const APP_STATUS_QUERY = `
  query AppStatus {
    app_config(where: { key: { _eq: "application_status" } }, limit: 1) {
      value
    }
  }
`;

async function fetchAppStatus(): Promise<AppStatus> {
  const now = Date.now();
  if (cachedStatus && now - lastFetch < STATUS_CACHE_MS) {
    return cachedStatus;
  }
  try {
        const { body } = await nhost.graphql.request({ query: APP_STATUS_QUERY });
           const data = body.data as { app_config?: { value: string }[] } | undefined;
       const value = data?.app_config?.[0]?.value;
    const status = (value === "maintenance" || value === "closed")
      ? value
      : "active";
    cachedStatus = status as AppStatus;
    lastFetch = now;
    return cachedStatus;
  } catch {
    // If fetch fails, use last known good status or default to active
    const stored = localStorage.getItem(STATUS_KEY);
    return (stored === "maintenance" || stored === "closed" ? stored : "active") as AppStatus;
  }
}

interface SiteStatusGuardProps {
  children: React.ReactNode;
}

const SiteStatusGuard = ({ children }: SiteStatusGuardProps) => {
  const location = useLocation();
  const [status, setStatus] = useState<AppStatus>("active");
  const [loading, setLoading] = useState(true);

  const isAdminPage = location.pathname.startsWith("/admin");
  const isDashboard = location.pathname.startsWith("/dashboard");
  const isForgotPassword = location.pathname.startsWith("/forgot-password");
  const isLogin = location.pathname.startsWith("/login");
  const isRegister = location.pathname.startsWith("/register");

  // Admin pages, dashboard, login, register, and forgot-password always bypass the maintenance gate
  const bypassGate = isAdminPage || isDashboard || isForgotPassword || isLogin || isRegister;

  useEffect(() => {
    if (bypassGate) {
      setStatus("active");
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchAppStatus().then((s) => {
      if (!cancelled) {
        setStatus(s);
        localStorage.setItem(STATUS_KEY, s);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [bypassGate, location.pathname]);

  if (loading || status === "active" || bypassGate) {
    return <>{children}</>;
  }

  if (status === "maintenance") {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
          {/* Friendly illustration */}
          <div className="w-full max-w-[400px] h-[160px] mx-auto mb-8 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Friendly%20warm%20flat%20vector%20illustration%20of%20two%20diverse%20people%20working%20together%20on%20a%20laptop%2C%20one%20wearing%20a%20yellow%20hard%20hat%2C%20high-five%20gesture%2C%20soft%20pastel%20amber%20and%20cream%20background%2C%20cartoon%20style%2C%20reassuring%20and%20approachable%2C%20clean%20minimal%20design%20with%20gentle%20warm%20tones%2C%20no%20text&width=800&height=320&seq=maintenance-illustration-01&orientation=landscape"
              alt="Our team is working on improvements"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Heading */}
          <h1 className="text-3xl font-extrabold text-foreground-900 mb-3 font-sora">
            We&apos;ll Be Right Back
          </h1>

          {/* Subtext */}
          <p className="text-foreground-500 text-sm leading-relaxed mb-8 max-w-sm mx-auto">
            Man Aya DataLink is currently under maintenance and will be back in a few hours. Thanks for your patience!
          </p>

          {/* WhatsApp CTA button */}
          <a
            href="https://whatsapp.com/channel/0029VbDBcHvFnSz8dDN9mP46"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full max-w-sm mx-auto bg-accent-500 hover:bg-accent-600 text-white font-bold text-base py-4 px-6 rounded-xl cursor-pointer transition-colors whitespace-nowrap mb-8"
          >
            <span className="flex items-center justify-center gap-3">
              <i className="ri-whatsapp-line text-xl w-6 h-6 flex items-center justify-center"></i>
              Join WhatsApp for Live Updates
            </span>
          </a>

          {/* Support email */}
          <p className="text-xs text-foreground-400">
            Need urgent help? Contact us at{" "}
            <a href="mailto:support@manayadatalink.com" className="text-primary-600 font-semibold">support@manayadatalink.com</a>
          </p>
        </div>
      </div>
    );
  }

  // status === "closed"
  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-6">
          <i className="ri-store-3-line text-red-500 text-3xl"></i>
        </div>
        <h1 className="text-2xl font-extrabold text-foreground-900 mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>
          Service Currently Unavailable
        </h1>
        <p className="text-foreground-500 text-sm leading-relaxed mb-6">
          Man Aya DataLink is temporarily closed. We apologize for the inconvenience. Please check back later or contact our support team for more information.
        </p>
        <p className="text-xs text-foreground-400">
          Contact us at{" "}
          <a href="mailto:support@manayadatalink.com" className="text-primary-600 font-semibold">support@manayadatalink.com</a>
        </p>
      </div>
    </div>
  );
};

export default SiteStatusGuard;